import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import axios from "axios";
import { Send, Sparkles, MessageCircle, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";
import { API } from "../lib/api";

export default function ConciergePage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { language } = useLanguage();
  const { location, locationData, guestType, guestTypeData } = useLocation();

  const translations = {
    en: {
      title: "Lina",
      subtitle: "Your local expert",
      intro: `Ciao! I'm Lina, your personal concierge for ${locationData?.name || 'Puglia'}.\n\nI know the best spots — the quiet beaches before 10am, the restaurants where locals actually eat, the experiences worth your time.\n\nWhat can I help you with today?`,
      placeholder: "Ask me anything...",
      quickPrompts: [
        "Best beach today?",
        "Where should I eat tonight?",
        "Plan my day",
        "Best sunset spot?"
      ],
      popular: "Popular questions",
      whatsappCta: "Prefer to chat? WhatsApp us",
      tip: "I'll give you 3-5 curated options, not a long list."
    },
    it: {
      title: "Lina",
      subtitle: "La tua esperta locale",
      intro: `Ciao! Sono Lina, la tua concierge personale per ${locationData?.name || 'la Puglia'}.\n\nConosco i posti migliori — le spiagge tranquille prima delle 10, i ristoranti dove mangiano davvero i locali, le esperienze che valgono il tuo tempo.\n\nCome posso aiutarti oggi?`,
      placeholder: "Chiedimi qualsiasi cosa...",
      quickPrompts: [
        "Miglior spiaggia oggi?",
        "Dove dovrei cenare stasera?",
        "Pianifica la mia giornata",
        "Miglior posto per il tramonto?"
      ],
      popular: "Domande popolari",
      whatsappCta: "Preferisci chattare? Scrivici su WhatsApp",
      tip: "Ti darò 3-5 opzioni curate, non una lunga lista."
    },
    fr: {
      title: "Lina",
      subtitle: "Votre experte locale",
      intro: `Ciao! Je suis Lina, votre concierge personnelle pour ${locationData?.name || 'les Pouilles'}.\n\nJe connais les meilleurs endroits — les plages calmes avant 10h, les restaurants où mangent vraiment les locaux, les expériences qui valent votre temps.\n\nComment puis-je vous aider aujourd'hui?`,
      placeholder: "Demandez-moi n'importe quoi...",
      quickPrompts: [
        "Meilleure plage aujourd'hui?",
        "Où devrais-je dîner ce soir?",
        "Planifiez ma journée",
        "Meilleur endroit pour le coucher du soleil?"
      ],
      popular: "Questions populaires",
      whatsappCta: "Préférez discuter? Écrivez-nous sur WhatsApp",
      tip: "Je vous donnerai 3-5 options choisies, pas une longue liste."
    },
    de: {
      title: "Lina",
      subtitle: "Ihre lokale Expertin",
      intro: `Ciao! Ich bin Lina, Ihre persönliche Concierge für ${locationData?.name || 'Apulien'}.\n\nIch kenne die besten Orte — die ruhigen Strände vor 10 Uhr, die Restaurants, in denen die Einheimischen wirklich essen, die Erlebnisse, die Ihre Zeit wert sind.\n\nWie kann ich Ihnen heute helfen?`,
      placeholder: "Fragen Sie mich alles...",
      quickPrompts: [
        "Bester Strand heute?",
        "Wo sollte ich heute Abend essen?",
        "Planen Sie meinen Tag",
        "Bester Sonnenuntergangs-Ort?"
      ],
      popular: "Beliebte Fragen",
      whatsappCta: "Lieber chatten? Schreiben Sie uns auf WhatsApp",
      tip: "Ich gebe Ihnen 3-5 ausgewählte Optionen, keine lange Liste."
    }
  };

  const txt = translations[language] || translations.en;

  const welcomeMessage = {
    role: "assistant",
    content: txt.intro
  };

  useEffect(() => {
    setMessages([welcomeMessage]);
    if (routerLocation.state?.prompt) {
      handleSend(routerLocation.state.prompt);
    }
  }, [language, locationData?.name]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (text = input) => {
    if (!text.trim()) return;
    
    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      // Add context about location and guest type to make responses more curated
      const contextualMessage = `[Context: User is staying in ${locationData?.name || 'Monopoli'}, guest type: ${guestTypeData?.label?.en || 'couple'}. Give exactly 3-5 curated, opinionated recommendations - not a list. Be confident and specific. Include why you recommend each option and practical details like price/timing. Speak like a local friend, not a chatbot.]\n\nUser question: ${text}`;
      
      const response = await axios.post(`${API}/chat`, {
        message: contextualMessage,
        session_id: sessionId,
        language: language
      });
      
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessages = {
        en: "I'm having a small technical moment. Try again in a second, or reach out on WhatsApp for immediate help.",
        it: "Ho un piccolo problema tecnico. Riprova tra un secondo, o contattaci su WhatsApp per aiuto immediato.",
        fr: "J'ai un petit problème technique. Réessayez dans une seconde, ou contactez-nous sur WhatsApp pour une aide immédiate.",
        de: "Ich habe ein kleines technisches Problem. Versuchen Sie es in einer Sekunde erneut oder kontaktieren Sie uns auf WhatsApp für sofortige Hilfe."
      };
      setMessages(prev => [...prev, { role: "assistant", content: errorMessages[language] || errorMessages.en }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24 md:pb-8" data-testid="concierge-page">
      {/* Header */}
      <header className="bg-gradient-to-b from-stone-900 to-stone-800 text-white px-6 pt-8 pb-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles size={28} />
          </div>
          <h1 className="text-3xl font-serif">{txt.title}</h1>
          <p className="text-stone-400 text-sm mt-1">{txt.subtitle} · {locationData?.name}</p>
          <p className="text-stone-500 text-xs mt-3 max-w-sm mx-auto">{txt.tip}</p>
        </div>
      </header>

      {/* Messages */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-4 rounded-2xl ${
              msg.role === "user" 
                ? "bg-teal-600 text-white rounded-br-md" 
                : "bg-white text-stone-700 rounded-bl-md shadow-sm border border-stone-100"
            }`}>
              <p className="text-[15px] leading-relaxed whitespace-pre-line">{msg.content}</p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white text-stone-700 p-4 rounded-2xl rounded-bl-md shadow-sm border border-stone-100">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick prompts - only show at start */}
      {messages.length <= 1 && (
        <section className="max-w-2xl mx-auto px-4 pb-4">
          <p className="text-stone-500 text-sm font-medium mb-3">{txt.popular}</p>
          <div className="flex flex-wrap gap-2">
            {txt.quickPrompts.map((prompt, i) => (
              <button
                key={i}
                onClick={() => handleSend(prompt)}
                className="bg-white border border-stone-200 hover:border-teal-400 text-stone-700 hover:text-teal-700 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
              >
                {prompt}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* WhatsApp CTA */}
      <section className="max-w-2xl mx-auto px-4 pb-4">
        <a 
          href={`https://wa.me/${locationData?.whatsapp || '393331234567'}`}
          className="block bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:bg-emerald-100 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="text-emerald-600" size={20} />
              <span className="text-emerald-800 font-medium text-sm">{txt.whatsappCta}</span>
            </div>
            <span className="text-emerald-600">→</span>
          </div>
        </a>
      </section>

      {/* Input */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 px-4 py-3 bg-stone-50 border-t border-stone-200">
        <div className="max-w-2xl mx-auto flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            placeholder={txt.placeholder}
            className="flex-1 bg-white border border-stone-200 rounded-xl px-5 py-3 text-[15px] focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            data-testid="chat-input"
          />
          <button
            onClick={() => handleSend()}
            disabled={isLoading || !input.trim()}
            className="bg-teal-600 hover:bg-teal-700 disabled:bg-stone-300 text-white w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
            data-testid="send-btn"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
